import SEO, { schemas } from '../components/SEO';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone } from 'lucide-react';

const SERVICES = [
  {
    id: 'nemt',
    icon: '🚑',
    title: 'Non-Emergency Medical Transportation (NEMT)',
    subtitle: 'Door-to-door medical ride service',
    image: '/images/van-ramp-navy.jpg',
    desc: "We provide door-to-door transportation for patients who require assistance getting to and from medical appointments. Whether you need a ride to a hospital, rehabilitation center, or specialist clinic, we ensure a comfortable and timely journey.",
    features: ["Doctor's Appointments", "Physical Therapy & Rehabilitation", "Dialysis & Chemotherapy Sessions", "Post-Hospital Discharge Transport"],
    note: "Ideal for seniors, individuals with mobility challenges, and patients recovering from illness or surgery.",
  },
  {
    id: 'wheelchair',
    icon: '♿',
    title: 'Wheelchair-Accessible Transportation',
    subtitle: 'ADA-compliant vehicles & trained drivers',
    image: '/images/van-interior-wide.jpg',
    desc: "Our vehicles are equipped with ramps and lifts to accommodate passengers who use wheelchairs, walkers, or mobility aids. Our trained team assists with boarding, securing, and disembarking.",
    features: ["Spacious ADA-compliant vehicles", "Trained mobility assistance drivers", "Safe wheelchair tie-down systems", "Walker and mobility aid support"],
    note: "Perfect for individuals with mobility impairments, including seniors and people with disabilities.",
  },
  {
    id: 'senior',
    icon: '👴',
    title: 'Senior & Assisted Transportation',
    subtitle: 'Dignity and independence for every senior',
    image: '/images/sienna-parking.jpg',
    desc: "Aging should never be a barrier to independence! We provide specialized transportation for seniors who need safe and convenient rides to various destinations.",
    features: ["Medical Check-Ups & Therapy", "Grocery & Pharmacy Visits", "Social & Family Gatherings", "Religious Services & Community Events"],
    note: "Ideal for elderly individuals who need extra care and support during transit.",
  },
  {
    id: 'discharge',
    icon: '🏥',
    title: 'Hospital Discharge & Recovery Transport',
    subtitle: 'Safe transitions from hospital to home',
    image: '/images/van-interior-cargo.jpg',
    desc: "Being discharged from the hospital? We provide comfortable and reliable rides to ensure a smooth transition from the hospital to home or a care facility.",
    features: ["Post-Surgery Transport", "Recovery & Rehabilitation Rides", "Assisted Boarding & Disembarking", "Care Facility Transfers"],
    note: "Designed for patients needing extra care during post-hospital transfers.",
  },
  {
    id: 'special',
    icon: '💙',
    title: 'Special Needs & Compassionate Care',
    subtitle: 'Patient-centered specialized transportation',
    image: '/images/fleet-silver-row.jpg',
    desc: "For individuals with chronic illnesses, disabilities, or special healthcare needs, we provide patient-centered transportation services that ensure comfort, care, and dignity during every ride.",
    features: ["Trained special needs support staff", "Personalized transport services", "Compassionate care approach", "Cognitive & behavioral support awareness"],
    note: "Ideal for individuals with autism, cognitive impairments, or other medical conditions.",
  },
  {
    id: 'recurring',
    icon: '🔁',
    title: 'Scheduled & Recurring Transportation',
    subtitle: 'Never miss an important appointment',
    image: '/images/van-ramp-silver-back.jpg',
    desc: "Need regular rides to medical appointments or therapy sessions? We offer scheduled and subscription-based transportation services, so you never have to worry about missing an important appointment.",
    features: ["Weekly or monthly transport plans", "Reliable scheduling & reminders", "Personalized route planning", "Flexible cancellation options"],
    note: "Best for patients with ongoing treatment plans or seniors requiring routine medical visits.",
  },
];

export default function ServicesPage() {
  return (
    <div>
      <SEO
        title="Our Services — NEMT, Wheelchair Transport & More"
        description="Explore MDS Transportation LLC's full range of NEMT services: wheelchair-accessible rides, senior transport, hospital discharge, special needs transport, and recurring medical appointment schedules in Indianapolis, IN."
        path="/services"
        keywords="NEMT services Indianapolis, wheelchair transport Indiana, senior medical rides, hospital discharge transportation, recurring dialysis transport, special needs transport Indianapolis"
        schema={schemas.services}
      />
      {/* Hero */}
      <section className="relative py-24 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/van-ramp-silver-back.jpg" alt="Services" className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-purple-300 font-semibold text-sm uppercase tracking-wider">What We Offer</span>
          <h1 className="text-5xl font-bold text-white mt-3 mb-6">Our Transportation Services</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Comprehensive non-emergency medical transportation for patients, seniors, and individuals with mobility challenges — delivered with compassion and professionalism.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {SERVICES.map((s, i) => (
            <div key={s.id} className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="text-4xl mb-3">{s.icon}</div>
                <span className="text-purple-700 font-semibold text-sm">{s.subtitle}</span>
                <h2 className="text-3xl font-bold text-gray-900 mt-1 mb-4 font-heading">{s.title}</h2>
                <p className="text-gray-600 leading-relaxed mb-6">{s.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {s.features.map(f => (
                    <div key={f} className="flex items-center gap-2">
                      <CheckCircle size={15} className="text-purple-700 shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 mb-6">
                  <p className="text-purple-800 text-sm">📌 {s.note}</p>
                </div>
                <Link to="/book" className="btn-primary inline-flex items-center gap-2 text-sm">Book This Service <ArrowRight size={16} /></Link>
              </div>
              <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                <img src={s.image} alt={s.title} className="rounded-3xl shadow-xl w-full h-72 object-cover" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Summary & CTA */}
      <section className="py-16 bg-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4 font-heading">Why Choose MDS Transportation?</h2>
              <div className="space-y-3">
                {['Safe & Reliable Service – Punctual and professional', 'Experienced & Caring Staff – Trained drivers', 'Wheelchair & Mobility Assistance', 'Flexible Booking – One-time or recurring', 'Affordable Rates – Transparent pricing'].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-green-300 shrink-0" />
                    <span className="text-purple-100 text-sm">{i}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">Book Your Ride Today</h3>
              <div className="space-y-3 text-purple-100 text-sm mb-6">
                <p className="flex items-center gap-2"><Phone size={14} /> +1 (317) 982-1959</p>
                <p>📧 info@mdstransportation.com</p>
                <p>📍 5632 W Washington St, Indianapolis, IN 46241</p>
              </div>
              <Link to="/book" className="bg-white text-purple-700 hover:bg-purple-50 font-semibold py-3 px-6 rounded-lg inline-flex items-center gap-2 transition-colors w-full justify-center">
                Book Online Now <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
